import gridView from '@/global/components/grid/GridView.vue'
import { api } from '@/global/services/api'
import { registeredTableHandlers } from '@/global/components/view-layouts/tabs-layout/config/registered-table-handlers'
import { dateService, FORMATS } from '@/global/services/date'
import TruncatedTextDisplay from '@/global/components/TruncatedTextDisplay.vue'
import { cloneDeep, getBooleanFromValue } from '@/global/utils/utils'
import violationsGridBlock from '@/modules/people-module/people/components/violationsGridBlock.vue'
import { showDetailsAction, deleteAction } from '@/global/components/table/helpers/tableActions'

export default {
  data () {
    return {
      showViolationsLoader: false,
      violationsPermissions: {}
    }
  },

  computed: {
    violationsConfig () {
      return {
        title: this.$t('people.violations'),
        key: 'violations',
        onTabChange: () => {
          this.showMainEditButtons = false
        },
        tabContent: [
          {
            component: this.tableGridComponent,
            editable: false,
            showLoader: this.showViolationsLoader,
            apiConfig: {
              get: {
                module: 'people',
                route: 'person-violations',
                method: 'get',
                queryParams: (event) => this.getViolationsIndexQueryParams(event)
              }
            },
            rowDblClick: (event) => this.onShowViolation(event),
            mobileConfig: {
              title: this.$t('people.violations')
            },
            title_and_back_config: {
              title: this.$t('people.violations')
            },
            search_and_buttons_config: {
              newClick: () => this.onNewViolation(),
              showNewBtn: getBooleanFromValue(this.violationsPermissions?.canCreate)
            },
            gridConfig: {
              component: gridView,
              blockConfig: {
                component: violationsGridBlock,
                onEdit: (event) => this.onShowViolation(event),
                onDelete: (event) => this.onViolationDelete(event)
              },
              useBorder: false
            },
            onDataReceivedWithParams: (event) => {
              this.violationsPermissions = event?.data?.meta?.permissions || {}
            },
            tableConfig: {
              cellsConfig: {
                date: {
                  custom_display_value: (item) => {
                    return item?.date ? dateService.formatCustom(item.date, FORMATS.isoDatetime, FORMATS.date) : '-'
                  }
                },
                vehicle: {
                  custom_display_value: (item) => `${
                    `${item?.vehicle?.registration || ''} (${item?.vehicle?.label || ''})`
                  }`
                },
                type: {
                  custom_display_value: (item) => `${
                    `${item?.type?.description || ''}`
                  }`
                }
              },
              headers: [
                {
                  key: 'vehicle',
                  label: this.$t('people/violations.vehicle'),
                  always_visible: true
                },
                {
                  key: 'date',
                  label: this.$t('people/violations.violation_date'),
                  always_visible: true
                },
                {
                  key: 'type',
                  label: this.$t('people/violations.violation_type'),
                  always_visible: true
                },
                {
                  key: 'note',
                  label: this.$t('people/violations.comment'),
                  always_visible: true,
                  component: TruncatedTextDisplay
                }
              ],
              actionsConfig: [
                showDetailsAction(this.$t, (event) => this.onShowViolation(event), { visible: getBooleanFromValue(this.violationsPermissions?.canRead) }),
                deleteAction(this.$t, (event) => this.onViolationDelete(event), { visible: getBooleanFromValue(this.violationsPermissions?.canDelete) })
              ],
              paginationConfig: {
                perPageOptions: [50, 100]
              },
              filtersConfig: {
                fields: [
                  {
                    key: 'vehicle_id',
                    label: this.$t('people/violations.vehicle'),
                    type: 'vehicles-selection',
                    maxSelect: 1,
                    clearable: true,
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'date',
                    label: this.$t('people/violations.violation_date'),
                    type: 'datetime-picker',
                    subType: 'date',
                    appendIcon: 'mdi-calendar-month',
                    format: FORMATS.date
                  },
                  {
                    key: 'type',
                    label: this.$t('people/violations.violation_type'),
                    type: 'autocomplete',
                    autocomplete_options: {
                      module: 'base',
                      route: 'codebook/autocomplete',
                      queryParams: {
                        type: 'FLEET_INCIDENT_VIOLATION_TYPE'
                      },
                      custom_display_value: (item) => {
                        return item?.description || null
                      }
                    }
                  }
                ]
              }
            }
          }
        ]
      }
    }
  },

  methods: {
    onNewViolation () {
      this.$set(this.manageViolationDialogConfig, 'title', this.$t('people/violations.adding_new_violation'))
      this.$set(this.manageViolationDialogConfig, 'createMode', true)
      this.$set(this.manageViolationDialogConfig, 'editData', null)
      this.$set(this.manageViolationDialogConfig, 'show', true)
    },

    async onShowViolation (event) {
      try {
        const { id: violationId } = event?.data || {}
        this.showViolationsLoader = true
        // do not show violation details if its id is not valid
        if (!violationId) {
          return
        }

        const { data } = await api('people').get(`person-violations/${violationId}`)

        const violationsData = data
        if (violationsData?.vehicle?.id) {
          violationsData.vehicle_id = [violationsData.vehicle.id]
        }

        const formattedDate = violationsData?.date ? dateService.formatCustom(violationsData.date, FORMATS.isoDate, FORMATS.date) : null
        this.$set(this.manageViolationDialogConfig, 'title', this.$t('people/violations.editing_violation') + (formattedDate ? ' - ' + formattedDate : ''))
        this.$set(this.manageViolationDialogConfig, 'createMode', false)
        this.$set(this.manageViolationDialogConfig, 'editData', data)
        this.$set(this.manageViolationDialogConfig, 'show', true)
      }
      catch (exception) {
        console.error(exception)
      }
      finally {
        this.showViolationsLoader = false
      }
    },

    onViolationDelete (event) {
      const { date } = event?.data || {}
      const formattedDate = date ? dateService.formatCustom(date, FORMATS.isoDatetime, FORMATS.date) : null

      this.$set(this.informationDialogConfig, 'title', this.$t('people/violations.delete_title') + (formattedDate ? ' - ' + formattedDate : ''))
      this.$set(this.informationDialogConfig, 'message', this.$t('people/violations.delete_message'))
      this.$set(this.informationDialogConfig, 'confirmCallbackFunction', async () => await this.deleteViolationCallback(event))
      this.$set(this.informationDialogConfig, 'showDialog', true)
    },

    async deleteViolationCallback (event) {
      try {
        const { id: deleteId } = event?.data || {}
        if (!deleteId) {
          return
        }

        await api('people').delete(`person-violations/${deleteId}`)
        const { fetchData } = registeredTableHandlers?.violations || {}
        if (typeof fetchData === 'function') {
          fetchData()
        }
        this.$set(this.informationDialogConfig, 'showDialog', false)
      }
      catch (exception) {
        console.error(exception)
      }
    },

    getViolationsIndexQueryParams (event) {
      const { params } = event || {}
      const newParams = cloneDeep(params)
      if (this.$route?.params?.id) {
        newParams.person_id = this.$route.params.id
      }
      if (newParams?.filters?.vehicle_id) {
        newParams.filters.vehicle_id = newParams.filters.vehicle_id?.[0]?.id || newParams.filters.vehicle_id?.[0]
      }

      return newParams
    }
  }
}
